import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CertificadosService, CertificadoRequest, CertificadoResponse } from '../../services/certificados/certificados.service';
import { DownloadModalComponent } from '../shared/download-modal/download-modal.component';
import { LoadingService } from '../../services/general/loading.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

interface CertificadoFormData {
  tipoCertificado: string;
  documentId: string;
  email: string;
  year: string;
}

@Component({
  selector: 'app-certificados',
  standalone: true,
  imports: [CommonModule, FormsModule, DownloadModalComponent, PageHeaderComponent],
  templateUrl: './certificados.component.html',
  styleUrl: './certificados.component.css'
})
export class CertificadosComponent implements OnInit {

  formData: CertificadoFormData = {
    tipoCertificado: '',
    documentId: '',
    email: '',
    year: ''
  };

  tiposCertificado = [
    { value: 'ingresos', label: 'Certificado de ingresos y retenciones (Para Proveedores)' },
    { value: 'cdt', label: 'Certificado Tributario CDT' },
    { value: 'cuentas-ahorro', label: 'Certificado Tributario de Cuenta Empresarial' },
    { value: 'credito', label: 'Certificado Tributario de Crédito' },
    { value: 'costos-totales', label: 'Certificado Anual de Costos' },
  ];

  availableYears: number[] = [];
  isLoading: boolean = false;
  showAlertError: boolean = false;
  showAlertSuccess: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  showResults: boolean = false;
  certificadoBase64: string = '';
  certificadoResponse: CertificadoResponse | null = null;
  showDownloadModal: boolean = false;
  downloadFileName: string = '';
  token: string = '';
  presignedUrl: string = '';

  constructor(
    private readonly _certificadosService: CertificadosService,
    private readonly _loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.generateAvailableYears();
    this.token = localStorage.getItem('authToken') ?? '';
    
    const lastSelectedYear = localStorage.getItem('lastSelectedYear');
    if (lastSelectedYear && this.availableYears.includes(parseInt(lastSelectedYear))) {
      this.formData.year = lastSelectedYear;
    } else if (this.availableYears.length > 0) {
      this.formData.year = this.availableYears[0].toString();
    }
  }


  generateAvailableYears(): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 1;
    const yearsCount = 10;

    this.availableYears = [];
    for (let i = 0; i < yearsCount; i++) {
      this.availableYears.push(startYear - i);
    }
  }


  onSubmit(form: any): void {
    if (form.invalid) {
      return;
    }

    // Guardar el año seleccionado en localStorage
    localStorage.setItem('lastSelectedYear', this.formData.year);

    this._loadingService.setLoading(true);
    this.isLoading = true;
    this.showAlertError = false;
    this.showAlertSuccess = false;
    this.showResults = false;

    if (['cdt', 'cuentas-ahorro', 'credito', 'costos-totales'].includes(this.formData.tipoCertificado)) {
      const requestBody: any = {
        document: this.formData.documentId,
        year: this.formData.year,
        ...(this.formData.tipoCertificado === 'costos-totales' && this.formData.email ? { email: this.formData.email } : {})
      };

      // Seleccionar el servicio correcto según el tipo
      let serviceCall;
      let certificateType = '';

      if (this.formData.tipoCertificado === 'cdt') {
        serviceCall = this._certificadosService.getCertificadoTributarioCDT(this.token, requestBody);
        certificateType = 'tributario';
      } else if (this.formData.tipoCertificado === 'cuentas-ahorro') {
        serviceCall = this._certificadosService.getCertificadoCuentasAhorro(this.token, requestBody);
        certificateType = 'de cuentas de ahorro';
      } else if (this.formData.tipoCertificado === 'costos-totales') {
        serviceCall = this._certificadosService.getCertificadoCostosTotales(this.token, requestBody);
        certificateType = 'de costos totales';
      } else {
        serviceCall = this._certificadosService.getCertificadoCredito(this.token, requestBody);
        certificateType = 'de crédito';
      }
      
      serviceCall.subscribe({
        next: (response: CertificadoResponse) => {
          this._loadingService.setLoading(false);
          this.isLoading = false;
          this.certificadoResponse = response;

          if (response.data && (response.data as Record<string, any>)['certificates']) {
            const certificates = (response.data as Record<string, any>)['certificates'];
            if (Array.isArray(certificates) && certificates.length > 0) {
              const certificate = certificates[0];
              this.presignedUrl = certificate.publicS3UrlFile;
              this.downloadFileName = certificate.fileName || `${this.formData.tipoCertificado}_${this.formData.documentId}_${this.formData.year}.pdf`;

              // Descargar inmediatamente — el backend incluye ResponseContentDisposition:attachment
              try {
                window.open(this.presignedUrl, '_blank');
                this.showAlertSuccess = true;
                this.successMessage = response.message || `Certificado ${certificateType} descargado exitosamente`;
                this.hideAlertAfterDelay('success');
              } catch (err) {
                this.showAlertError = true;
                this.errorMessage = `Error al descargar el certificado ${certificateType}`;
                this.hideAlertAfterDelay('error');
              }
            } else {
              this.showAlertError = true;
              this.errorMessage = `No se pudo obtener el certificado ${certificateType}`;
              this.hideAlertAfterDelay('error');
            }
          } else {
            this.showAlertError = true;
            this.errorMessage = `No se pudo obtener el certificado ${certificateType}`;
            this.hideAlertAfterDelay('error');
          }
        },
        error: (error) => {
          this._loadingService.setLoading(false);
          this.isLoading = false;
          this.showAlertError = true;
          
          // Extraer y parsear el error
          const parsedError = this.parseErrorResponse(error);
          
          // Verificar si es el error de "no se encontraron datos"
          if (this.isNotFoundDataError(parsedError)) {
            this.errorMessage = `No se encontraron datos para generar el certificado ${certificateType}. Verifica el número de documento y el año seleccionado.`;
          } else {
            this.errorMessage = this.extractErrorMessage(parsedError, error);
          }
          
          this.hideAlertAfterDelay('error');
          console.error(`Error al generar certificado ${certificateType}:`, error);
        }
      });
      return;
    }

    // Lógica original para certificado de ingresos
    const requestData: CertificadoRequest = {
      documentId: this.formData.documentId,
      email: this.formData.email || '',
      year: this.formData.year
    };

    this._certificadosService.getCertificado(this.token, requestData).subscribe({
      next: (response: CertificadoResponse) => {
        this._loadingService.setLoading(false);
        this.isLoading = false;
        this.certificadoResponse = response;
        if (response.data) {
          const data = response.data;
          if (data.file) {
            this.certificadoBase64 = data.file;
          } else if (data.base64) {
            this.certificadoBase64 = data.base64;
          } else if (data.content) {
            this.certificadoBase64 = data.content;
          } else if (typeof data === 'string') {
            this.certificadoBase64 = data;
          } else {
            const base64Key = Object.keys(data).find(key =>
              key.toLowerCase().includes('file') ||
              key.toLowerCase().includes('base64') ||
              key.toLowerCase().includes('content') ||
              key.toLowerCase().includes('pdf')
            );
            if (base64Key) {
              this.certificadoBase64 = data[base64Key];
            }
          }
        }

        if (this.certificadoBase64) {
          this.downloadFileName = `certificado_${this.formData.documentId}_${this.formData.year}.pdf`;
          this.showDownloadModal = true;
          this.showAlertSuccess = true;
          this.successMessage = response.message || 'Certificado generado exitosamente';
          this.hideAlertAfterDelay('success');
        } else {
          this.showAlertError = true;
          this.errorMessage = 'No se pudo obtener el archivo del certificado';
          this.hideAlertAfterDelay('error');
        }
      },
      error: (error) => {
        this._loadingService.setLoading(false);
        this.isLoading = false;
        this.showAlertError = true;
        if (error.error?.error) {
          this.errorMessage = error.error.error;
        } else if (error.error?.message) {
          this.errorMessage = error.error.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = 'Error al generar el certificado. Por favor, intenta nuevamente.';
        }

        this.hideAlertAfterDelay('error');
        console.error('Error al generar certificado:', error);
      }
    });
  }


  downloadFile(): void {
    // Si es CDT, abrir URL pre-firmada
    if (this.presignedUrl) {
      try {
        window.open(this.presignedUrl, '_blank');
        this.showAlertSuccess = true;
        this.successMessage = 'Certificado abierto en una nueva pestaña';
        this.hideAlertAfterDelay('success');
      } catch (error) {
        this.showAlertError = true;
        this.errorMessage = 'Error al abrir el certificado';
        this.hideAlertAfterDelay('error');
        console.error('Error al abrir certificado:', error);
      }
      return;
    }

    // Si es certificado de ingresos, descargar base64
    if (!this.certificadoBase64) {
      this.showAlertError = true;
      this.errorMessage = 'No hay certificado disponible para descargar';
      this.hideAlertAfterDelay('error');
      return;
    }

    try {
      let base64Data = this.certificadoBase64;
      if (base64Data.includes('base64,')) {
        base64Data = base64Data.split('base64,')[1];
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });


      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado_${this.formData.documentId}_${this.formData.year}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.showAlertSuccess = true;
      this.successMessage = 'Certificado descargado exitosamente';
      this.hideAlertAfterDelay('success');
    } catch (error) {
      this.showAlertError = true;
      this.errorMessage = 'Error al descargar el certificado';
      this.hideAlertAfterDelay('error');
      console.error('Error al descargar certificado:', error);
    }
  }


  cancel(form: any): void {
    form.reset();
    this.formData = {
      tipoCertificado: '',
      documentId: '',
      email: '',
      year: ''
    };
    this.showResults = false;
    this.certificadoBase64 = '';
    this.certificadoResponse = null;
    this.showAlertError = false;
    this.showAlertSuccess = false;
    this.showDownloadModal = false;
    this.presignedUrl = '';
  }


  closeDownloadModal(): void {
    this.showDownloadModal = false;
  }

  handleDownload(): void {
    this.downloadFile();
    this.showDownloadModal = false;
  }


  private hideAlertAfterDelay(type: 'error' | 'success', delay: number = 8000): void {
    setTimeout(() => {
      if (type === 'error') {
        this.showAlertError = false;
      } else {
        this.showAlertSuccess = false;
      }
    }, delay);
  }

  private parseErrorResponse(error: any): any {
    let errorBody = error.error || {};
    
    // Si error.error es un string JSON, parsearlo
    if (typeof errorBody === 'string') {
      try {
        errorBody = JSON.parse(errorBody.trim());
      } catch {
        return { rawString: errorBody };
      }
    }
    
    // Si errorBody tiene error como string JSON, parsearlo también
    if (errorBody.error && typeof errorBody.error === 'string') {
      try {
        return JSON.parse(errorBody.error.trim());
      } catch {
        return errorBody;
      }
    }
    
    return errorBody;
  }

  private isNotFoundDataError(errorBody: any): boolean {
    const errorTitle = errorBody.title || '';
    const errorDetail = errorBody.detail || '';
    const rawString = errorBody.rawString || '';
    
    return errorTitle === 'not found data' || 
           errorDetail === 'data not found to generate certificate' ||
           rawString.includes('not found data') ||
           rawString.includes('data not found to generate certificate');
  }

  private extractErrorMessage(errorBody: any, originalError: any): string {
    if (errorBody.error && typeof errorBody.error === 'string') {
      return errorBody.error;
    }
    if (errorBody.message) {
      return errorBody.message;
    }
    if (errorBody.detail) {
      return errorBody.detail;
    }
    if (originalError.message) {
      return originalError.message;
    }
    return 'Error al generar el certificado tributario. Por favor, intenta nuevamente.';
  }
}
